import React from 'react';
import { connect } from 'react-redux';

import { createComponent, enums, Header, Label, Layout, PasswordField, ProgressBar, Button, Input } from 'console-kit';

import RecoverPasswordPanelSelectors from '../redux/selectors';
import RecoverPasswordPanelActions from '../redux/actions';

import authStatics from '../../../common/static';
import authEnums from '../../../common/enums';

import { pwdStengthEvaluation, rePasswordValidationHelper } from '../../../common/utils';
import AuthImageLogo from '../../../components/AuthImageLogo';

const RecoverPasswordPanel = createComponent({
  displayName: 'RecoverPasswordPanel',
  refs:        'recoverPasswordFormRef',
  state:       {
    passwordValidation: {
      value: {
        rePasswordCorrect:      true,
        rePasswordErrorMessage: undefined,

        passwordErrorMessage: undefined,
        passwordCorrect:      true,
      },
      updater: 'setPasswordValidation',
    },
  },

  handlers: {
    handlePasswordFieldBlur: props => () => {
      const _minPwdLength = props.isSupportUser ?
        authEnums.pwdStrengthLimits.SUPPORT_MIN_LENGTH :
        authEnums.pwdStrengthLimits.MIN_LENGTH;

      const { isPasswordValid } = pwdStengthEvaluation(props.password, _minPwdLength);

      props.setPasswordValidation({
        ...props.passwordValidation,
        passwordErrorMessage: isPasswordValid ? '' : 'Invalid Password',
        passwordCorrect:      isPasswordValid,
      });
    },

    handleRePasswordFieldBlur: props => () => {
      const _minPwdLength = props.isSupportUser ?
        authEnums.pwdStrengthLimits.SUPPORT_MIN_LENGTH :
        authEnums.pwdStrengthLimits.MIN_LENGTH;

      const { isPasswordValid } = pwdStengthEvaluation(props.password, _minPwdLength);

      const { rePasswordErrorMessage, rePasswordCorrect } = rePasswordValidationHelper(
        props.password,
        props.repassword,
        isPasswordValid
      );

      props.setPasswordValidation({
        ...props.passwordValidation,
        rePasswordCorrect:      rePasswordCorrect,
        rePasswordErrorMessage: rePasswordErrorMessage,
      });
    },

    handleValueChange: p => (e, { id, value }) => {
      p.onUpdateField({ fieldId: id, value });
    },

    handleClick: props => (e, { value }) => {
      e.preventDefault();

      const _minPwdLength = props.isSupportUser ?
        authEnums.pwdStrengthLimits.SUPPORT_MIN_LENGTH :
        authEnums.pwdStrengthLimits.MIN_LENGTH;

      const { isPasswordValid } = pwdStengthEvaluation(props.password, _minPwdLength);

      const { rePasswordErrorMessage, rePasswordCorrect } = rePasswordValidationHelper(
        props.password,
        props.repassword,
        isPasswordValid
      );

      props.setPasswordValidation({
        rePasswordCorrect:      rePasswordCorrect,
        rePasswordErrorMessage: rePasswordErrorMessage,
        passwordErrorMessage:   isPasswordValid ? '' : 'Invalid Password',
        passwordCorrect:        isPasswordValid,
      });

      props.onRecoverPasswordClicked(props.refs.recoverPasswordFormRef, isPasswordValid && rePasswordCorrect);
    },
  },
  render(props) {
    const _minPwdLength = props.isSupportUser ?
      authEnums.pwdStrengthLimits.SUPPORT_MIN_LENGTH :
      authEnums.pwdStrengthLimits.MIN_LENGTH;

    const { progressBarLabel, progressBarRank, progressBarColor, isPasswordValid } = pwdStengthEvaluation(
      props.password,
      _minPwdLength
    );

    return (
      <div className={authStatics.SCREEN_CLASSES_WIDE}>
        <form ref={props.setRecoverPasswordFormRef} action={authStatics.RECOVERPASSWORD_FORM_SUBMIT_URL}>
          {/*user for user selection in the BE activation process*/}
          <input type="hidden" value={props.userId} name="id" />

          <Layout>
            <Layout.Row>
              <Layout.Column textAlign={enums.textAlignments.CENTER}>
                <AuthImageLogo />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <Layout.Column textAlign={enums.textAlignments.LEFT}>
                <Header
                  text="Set a new password"
                  isDividing={false}
                  font={{
                    emphasis: enums.font.emphasis.NORMAL,
                  }}
                  size={enums.sizes.X6}
                />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <Layout.Column textAlign={enums.textAlignments.LEFT}>
                <Label
                  text={`Your password must be at least ${
                    _minPwdLength
                  } characters long. For security reasons, we recommend to use a mixture of letters, numbers and special characters. Only good or strong passwords will be accepted.`}
                  hasPadding={false}
                  display={enums.display.PLAIN}
                  size={enums.sizes.X4}
                  isFluid
                />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <Layout.Column>
                <Input label="" isFluid={true} isDisabled={true} value={props.userEmail} />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <Layout.Column>
                <PasswordField
                  label="New Password"
                  isFluid={true}
                  hasError={!props.passwordValidation.passwordCorrect}
                  errorMessage={props.passwordValidation.passwordErrorMessage}
                  placeholder=""
                  id="password"
                  name="password"
                  isUnderlined={false}
                  onChange={props.handleValueChange}
                  onBlur={props.handlePasswordFieldBlur}
                  value={props.password}
                  revealTooltipTextDisabled={authStatics.pwdToggleTooltip}
                />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <Layout.Column>
                <PasswordField
                  label="Confirm Password"
                  hasError={!props.passwordValidation.rePasswordCorrect}
                  errorMessage={props.passwordValidation.rePasswordErrorMessage}
                  isFluid={true}
                  placeholder=""
                  id="repassword"
                  name="repassword"
                  isUnderlined={false}
                  onChange={props.handleValueChange}
                  onBlur={props.handleRePasswordFieldBlur}
                  value={props.repassword}
                  revealTooltipTextDisabled={authStatics.pwdToggleTooltip}
                />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <ProgressBar
                percentComplete={progressBarRank}
                size={enums.sizes.MEDIUM}
                color={progressBarColor}
                label={progressBarLabel}
              />
            </Layout.Row>

            <Layout.Row>
              <Button.WithAnimation
                actions={{
                  start:   RecoverPasswordPanelActions.onRecoverPasswordRequest,
                  success: RecoverPasswordPanelActions.onRecoverPasswordSuccess, //no action
                  failure: RecoverPasswordPanelActions.onRecoverPasswordFailure, //no action
                }}
              >
                <Button.Primary
                  text="Save"
                  float={enums.positions.RIGHT}
                  size={enums.sizes.X4}
                  onClick={props.handleClick}
                  textAlign={enums.textAlignments.CENTER}
                  color={enums.colors.ACCENT}
                  isSolid
                  isFluid
                />
              </Button.WithAnimation>
            </Layout.Row>
          </Layout>
        </form>
      </div>
    );
  },
});

export default connect(
  state => {
    return {
      userEmail:     RecoverPasswordPanelSelectors.userEmail(state),
      userId:        RecoverPasswordPanelSelectors.userId(state),
      isSupportUser: RecoverPasswordPanelSelectors.isSupportUser(state),

      password:   RecoverPasswordPanelSelectors.password(state),
      repassword: RecoverPasswordPanelSelectors.repassword(state),
    };
  },
  dispatch => ({
    onUpdateField({ fieldId, value }) {
      dispatch(RecoverPasswordPanelActions.updateField(fieldId, value));
    },

    onRecoverPasswordClicked(formRef, isValid) {
      dispatch(RecoverPasswordPanelActions.onRecoverPasswordRequest(formRef, isValid));
    },
  })
)(RecoverPasswordPanel);
